import React from 'react'
import type { CardWriteHandler } from '../../types/cardWrite'
import { Box } from '@mui/material'
import { DashboardCell, DashboardConfig, MultiTableAnalysisWidgetData } from '../../types/gotypes'
import { renderTextWidget } from './widgets/TextWidget'
import DashboardLayoutComponent from './DashboardLayoutComponent'
import { MultiTableAnalysisWidgetRenderer } from './widgets/MultiTableAnalysisWidget'
import { ImageWidgetRenderer } from './widgets/ImageWidget'
import { VideoWidgetRenderer } from './widgets/VideoWidget'
import { TableGraphWidgetRenderer } from './widgets/TableGraphWidget'
import { AgentArtifactWidgetRenderer } from './widgets/AgentArtifactWidget'
import { TitleHeroWidgetRenderer } from './widgets/TitleHeroWidget'
import { KPICardWidgetRenderer } from './widgets/KPICardWidget'
import { CalloutWidgetRenderer } from './widgets/CalloutWidget'
import { TOCWidgetRenderer } from './widgets/TOCWidget'
import {
  ReportPillarWidgetRenderer,
  pillarEntriesToLayout,
  replacePillarEntryCell,
} from './widgets/ReportPillarWidget'
import { widgetDomId } from '../../utils/dashboardWidgetWalk'
import { STACK_BREAKPOINT } from '../../utils/dashboardBuilderLayout'
import { cardGlobalFilter } from '../../utils/dashboardFilters'

interface DashboardCellComponentProps {
  cell: DashboardCell
  width: number
  customer?: string
  job?: string
  dashboardId?: string
  readOnly?: boolean
  /** #1219 gate 2 — see DashboardLayoutComponent.tsx's doc. */
  canRegenerate?: boolean
  globalFilter?: string
  jobFilters?: Record<string, string>
  colorMap?: Record<string, string>
  /** Theme id → flattened colour map (#1193) — resolves a chart's own brand_palette_key. */
  themeColorMaps?: Record<string, Record<string, string>>
  dashboardConfig?: DashboardConfig
  currentPageIndex?: number
  onCellUpdate?: (cell: DashboardCell) => void | Promise<void>
  /** #1256: narrow card text/sign-off write for any logged-in user; passed straight through. */
  onCardWrite?: CardWriteHandler
  /** This widget's address (#969 Part F) — see dashboardWidgetWalk.ts. Used
   *  as the DOM id the Review Changes panel scrolls to, and as the key into
   *  `changedWidgetPaths` for the per-widget dot indicator. */
  cellPath?: string
  /** Review mode (#969 Part F) — the dot indicator renders only here, never
   *  in plain view mode. */
  reviewMode?: boolean
  changedWidgetPaths?: Set<string>
}

const DashboardCellComponent: React.FC<DashboardCellComponentProps> = ({
  cell,
  width,
  customer,
  job,
  dashboardId,
  readOnly,
  canRegenerate,
  globalFilter,
  jobFilters,
  colorMap,
  themeColorMaps,
  dashboardConfig,
  currentPageIndex,
  onCellUpdate,
  onCardWrite,
  cellPath,
  reviewMode,
  changedWidgetPaths,
}) => {
  const renderContent = () => {
    switch (cell.widget_type) {
      case 'text':
        return renderTextWidget(cell.text_data)
      case 'layout':
        if (cell.layout_data) {
          return (
            <DashboardLayoutComponent
              layout={cell.layout_data}
              customer={customer}
              job={job}
              dashboardId={dashboardId}
              readOnly={readOnly}
              canRegenerate={canRegenerate}
              globalFilter={globalFilter}
              jobFilters={jobFilters}
              colorMap={colorMap}
              themeColorMaps={themeColorMaps}
              dashboardConfig={dashboardConfig}
              currentPageIndex={currentPageIndex}
              pathPrefix={cellPath}
              reviewMode={reviewMode}
              changedWidgetPaths={changedWidgetPaths}
              onCellUpdate={onCellUpdate ? (rowIndex, cellIndex, updatedInnerCell) => {
                const newRows = cell.layout_data!.rows.map((row, ri) => {
                  if (ri !== rowIndex) return row
                  return { ...row, cells: row.cells.map((c, ci) => ci === cellIndex ? updatedInnerCell : c) }
                })
                return onCellUpdate({ ...cell, layout_data: { ...cell.layout_data!, rows: newRows } })
              } : undefined}
              onCardWrite={onCardWrite}
            />
          )
        }
        return <div>Empty nested layout</div>
      case 'report_pillar': {
        // #1080. The pillar projects its entries into the layout shape they
        // already had — one row per entry — and renders through the SAME
        // layout renderer a nested layout used. View mode, edit mode and the
        // shared/logged-out view therefore all produce identical output, and
        // entry k keeps its "-k-0" widget address.
        if (!cell.report_pillar_data) return <div>Report pillar not configured</div>
        const pillarData = cell.report_pillar_data
        return (
          <ReportPillarWidgetRenderer data={pillarData}>
            <DashboardLayoutComponent
              layout={pillarEntriesToLayout(pillarData)}
              customer={customer}
              job={job}
              dashboardId={dashboardId}
              readOnly={readOnly}
              canRegenerate={canRegenerate}
              globalFilter={globalFilter}
              jobFilters={jobFilters}
              colorMap={colorMap}
              themeColorMaps={themeColorMaps}
              dashboardConfig={dashboardConfig}
              currentPageIndex={currentPageIndex}
              pathPrefix={cellPath}
              reviewMode={reviewMode}
              changedWidgetPaths={changedWidgetPaths}
              onCellUpdate={onCellUpdate ? (entryIndex, _cellIndex, updatedEntryCell) => {
                return onCellUpdate({
                  ...cell,
                  report_pillar_data: replacePillarEntryCell(pillarData, entryIndex, updatedEntryCell),
                })
              } : undefined}
              onCardWrite={onCardWrite}
            />
          </ReportPillarWidgetRenderer>
        )
      }
      case 'multi_table_analysis':
        if (cell.multi_table_analysis_data && customer) {
          // Use job from widget data, fallback to passed job
          const widgetJob = cell.multi_table_analysis_data.job || job || ''
          return (
            <MultiTableAnalysisWidgetRenderer
              data={cell.multi_table_analysis_data}
              customer={customer}
              job={widgetJob}
              dashboardId={dashboardId}
              readOnly={readOnly}
              canRegenerate={canRegenerate}
              globalFilter={cardGlobalFilter(widgetJob, job, jobFilters, globalFilter)}
              jobFilters={jobFilters}
              onUpdateData={onCellUpdate ? (updatedData: MultiTableAnalysisWidgetData) => {
                return onCellUpdate({ ...cell, multi_table_analysis_data: updatedData })
              } : undefined}
              onCardWrite={onCardWrite}
            />
          )
        }
        return <div>Multi-table analysis not configured</div>
      case 'image':
        if (cell.image_data && customer) {
          return <ImageWidgetRenderer data={cell.image_data} customer={customer} />
        }
        return <div>Image not configured</div>
      case 'video':
        if (cell.video_data && customer) {
          return <VideoWidgetRenderer data={cell.video_data} customer={customer} />
        }
        return <div>Video not configured</div>
      case 'table_graph':
        if (cell.table_graph_data && customer) {
          const tgJob = cell.table_graph_data.job || job || ''
          return (
            <TableGraphWidgetRenderer
              data={cell.table_graph_data}
              customer={customer}
              job={tgJob}
              readOnly={readOnly}
              globalFilter={globalFilter}
              jobFilters={jobFilters}
              colorMap={colorMap}
              themeColorMaps={themeColorMaps}
            />
          )
        }
        return <div>Table/Graph not configured</div>
      case 'agent_artifacts':
        if (cell.agent_artifact_data) {
          return <AgentArtifactWidgetRenderer data={cell.agent_artifact_data} />
        }
        return <div>Agent artifacts not configured</div>
      case 'title_hero':
        if (cell.title_hero_data) {
          return <TitleHeroWidgetRenderer data={cell.title_hero_data} />
        }
        return <div>Title not configured</div>
      case 'toc':
        if (cell.toc_data) {
          return (
            <TOCWidgetRenderer
              data={cell.toc_data}
              dashboardConfig={dashboardConfig}
              currentPageIndex={currentPageIndex}
            />
          )
        }
        return <div>Table of contents not configured</div>
      case 'kpi_card':
        if (cell.kpi_card_data) {
          return <KPICardWidgetRenderer data={cell.kpi_card_data} />
        }
        return <div>KPI card not configured</div>
      case 'callout':
        if (cell.callout_data) {
          return <CalloutWidgetRenderer data={cell.callout_data} />
        }
        return <div>Callout not configured</div>
      case 'spacer':
        // #1187: a spacer is deliberately nothing in view mode — transparent
        // empty space, no border, no chrome. It fills whatever height its row
        // has (fit-to-cell), which is the whole feature. The edit-mode outline
        // lives in DashboardEditorRow, never here, so the shared/public views
        // render pure space too.
        return <Box aria-hidden sx={{ flex: 1 }} />
      default:
        return <div>Unknown widget type: {cell.widget_type}</div>
    }
  }

  const hasUnreviewedChange = reviewMode && cellPath ? Boolean(changedWidgetPaths?.has(cellPath)) : false

  return (
    <Box
      id={cellPath ? widgetDomId(cellPath) : undefined}
      sx={{
        // Below the stacking breakpoint the row is one column, so every widget
        // is full width — see DashboardRowComponent.
        width: { xs: '100%', [STACK_BREAKPOINT]: `${width}%` },
        minWidth: 0,
        p: 1,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        ...(hasUnreviewedChange && {
          transition: 'box-shadow 0.2s',
          boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.warning.main}`,
          borderRadius: 1,
        }),
      }}
    >
      {/* Per-widget change indicator (#969 Part F) — review mode only, never
          in plain view mode; a small dot rather than a banner so the content
          surface stays clean (see the issue's "Carbon alignment" note). */}
      {hasUnreviewedChange && (
        <Box
          aria-label="Unreviewed change on this widget"
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: 'warning.main',
            zIndex: 1,
          }}
        />
      )}
      {renderContent()}
    </Box>
  )
}

export default DashboardCellComponent
